import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Menu as MenuIcon,
  ViewSidebar,
  AutoAwesome,
  AccountCircle,
  Logout,
} from '@mui/icons-material';
import { Avatar, IconButton, Menu, MenuItem, Divider, ListItemIcon, ListItemText, Typography } from '@mui/material';
import './workflowLayout.css';
import IconRail from './IconRail';
import ClientProjectPanel from './ClientProjectPanel';
import ProjectWorkflowTabs from './ProjectWorkflowTabs';
import ProjectForm from '../projects/ProjectForm';
import NotificationBell from '../notifications/NotificationBell';
import LoadingSpinner from '../common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { useProjectWorkflow } from '../../contexts/ProjectWorkflowContext';
import { buildAdminRailItems, buildAgencyRailItems, buildClientRailItems } from './workflowNav';
import { clientService } from '../../services/clientService';
import { projectService } from '../../services/projectService';
import { priceChangeRequestService } from '../../services/priceChangeRequestService';
import { pendingSitesService } from '../../services/pendingSitesService';
import { competitorBacklinksService } from '../../services/competitorBacklinksService';
import { CreateProjectData, Project, UpdateProjectData } from '../../types';
import {
  completeProjectCreation,
} from '../../utils/workflowPanelCache';

interface WorkflowLayoutProps {
  children: React.ReactNode;
}

const WorkflowLayout: React.FC<WorkflowLayoutProps> = ({ children }) => {
  const { user, logout, isSuperAdmin, isAdmin, isAgency, isClient, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    selectedProject,
    activeStep,
    panelOpen,
    setPanelOpen,
    togglePanel,
    selectProject,
    focusClientInPanel,
    clearProject,
    workflowEntryPoint,
  } = useProjectWorkflow();

  const urlProjectId = useMemo(
    () => new URLSearchParams(location.search).get('projectId'),
    [location.search]
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [projectFormOpen, setProjectFormOpen] = useState(false);
  const [projectFormClientId, setProjectFormClientId] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const openProjectForm = (clientId?: string) => {
    if (clientId) {
      setProjectFormClientId(clientId);
      focusClientInPanel(clientId);
    } else {
      setProjectFormClientId('');
    }
    setProjectFormOpen(true);
  };

  const closeProjectForm = () => {
    setProjectFormOpen(false);
    setProjectFormClientId('');
  };

  const clientIdForProjects = isClient ? user?.clientId : undefined;

  const { data: clientProjectsData, isLoading: clientProjectsLoading } = useQuery({
    queryKey: ['wf-client-project-count', clientIdForProjects],
    queryFn: () =>
      projectService.getProjects({
        clientId: clientIdForProjects,
        limit: 1,
      }),
    enabled: Boolean(isClient && clientIdForProjects),
  });

  const clientHasProjects = (clientProjectsData?.pagination?.total ?? clientProjectsData?.data?.length ?? 0) > 0;
  const clientGateActive = isClient && !clientProjectsLoading && !clientHasProjects;

  const { data: adminClientsData, isLoading: adminClientsLoading } = useQuery({
    queryKey: ['wf-admin-has-clients'],
    queryFn: () => clientService.getClients({ limit: 1 }),
    enabled: isAdmin || isSuperAdmin,
  });
  const adminHasClients = (adminClientsData?.pagination?.total ?? adminClientsData?.data?.length ?? 0) > 0;

  const { data: agencyManagedClientsData, isLoading: agencyManagedClientsLoading } = useQuery({
    queryKey: ['wf-agency-managed-clients'],
    queryFn: () => clientService.getClients({ limit: 500, excludeAgencySelfProfile: true }),
    enabled: isAgency,
  });

  const { data: agencyAllClientsData, isLoading: agencyAllClientsLoading } = useQuery({
    queryKey: ['wf-agency-all-clients'],
    queryFn: () => clientService.getClients({ limit: 500 }),
    enabled: isAgency,
  });

  const agencySelfProfileClient = useMemo(() => {
    if (!isAgency || !user?.email) return undefined;
    return agencyAllClientsData?.data?.find(
      (c) => c.email?.toLowerCase() === user.email.toLowerCase(),
    );
  }, [isAgency, user?.email, agencyAllClientsData]);

  const { data: agencySelfProjectsData, isLoading: agencySelfProjectsLoading } = useQuery({
    queryKey: ['wf-agency-self-projects', agencySelfProfileClient?.id],
    queryFn: () =>
      projectService.getProjects({
        clientId: agencySelfProfileClient!.id,
        limit: 1,
      }),
    enabled: isAgency && Boolean(agencySelfProfileClient?.id),
  });

  const hasManagedClients =
    (agencyManagedClientsData?.pagination?.total ?? agencyManagedClientsData?.data?.length ?? 0) > 0;
  const hasLegacySelfProjects =
    (agencySelfProjectsData?.pagination?.total ?? agencySelfProjectsData?.data?.length ?? 0) > 0;

  const { data: agencyProjectsData, isLoading: agencyProjectsLoading } = useQuery({
    queryKey: ['wf-agency-project-count'],
    queryFn: () => projectService.getProjects({ limit: 1 }),
    enabled: isAgency,
  });

  const {
    agencyGateNoClients,
    agencyGateNoProjects,
    agencyGateActive,
    firstAgencyClientId,
    agencyOnboardingLoading,
  } = useMemo(() => {
    const agencyHasProjects =
      (agencyProjectsData?.pagination?.total ?? agencyProjectsData?.data?.length ?? 0) > 0;
    const onboardingLoading =
      isAgency &&
      (agencyManagedClientsLoading ||
        agencyAllClientsLoading ||
        (agencySelfProfileClient && agencySelfProjectsLoading) ||
        agencyProjectsLoading);
    const gateNoClients =
      isAgency && !onboardingLoading && !hasManagedClients && !hasLegacySelfProjects;
    const gateNoProjects =
      isAgency && !onboardingLoading && !gateNoClients && hasManagedClients && !agencyHasProjects;
    return {
      agencyGateNoClients: gateNoClients,
      agencyGateNoProjects: gateNoProjects,
      agencyGateActive: gateNoClients || gateNoProjects,
      firstAgencyClientId: agencyManagedClientsData?.data?.[0]?.id as string | undefined,
      agencyOnboardingLoading: onboardingLoading,
    };
  }, [
    isAgency,
    agencyManagedClientsLoading,
    agencyAllClientsLoading,
    agencySelfProfileClient,
    agencySelfProjectsLoading,
    hasManagedClients,
    hasLegacySelfProjects,
    agencyProjectsLoading,
    agencyProjectsData,
    agencyManagedClientsData,
  ]);

  const { data: pendingCountData } = useQuery({
    queryKey: ['pending-price-requests-count'],
    queryFn: () => priceChangeRequestService.getPendingCount(),
    enabled: isSuperAdmin,
    refetchInterval: 30000,
  });

  const { data: pendingSitesStats } = useQuery({
    queryKey: ['pending-sites-stats'],
    queryFn: () => pendingSitesService.getStats(),
    enabled: isSuperAdmin,
    refetchInterval: 30000,
  });

  const { data: drCalcPendingCountData } = useQuery({
    queryKey: ['dr-calculator-pending-count'],
    queryFn: () => competitorBacklinksService.getDrCalculatorPendingRequestsCount(),
    enabled: isSuperAdmin,
    refetchInterval: 30000,
  });

  const { data: guestBlogArrivedCountData } = useQuery({
    queryKey: ['guest-blog-arrived-request-count'],
    queryFn: () => competitorBacklinksService.getUnseenGuestBlogArrivedCount(),
    enabled: isClient,
    refetchInterval: 45000,
  });

  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-project-form'],
    queryFn: () => clientService.getClients({ limit: 1000 }),
    enabled: (isAdmin || isSuperAdmin || isAgency) && projectFormOpen,
  });

  const railItems = useMemo(() => {
    if (isClient) {
      return buildClientRailItems({
        guestBlogArrivedCount:
          typeof guestBlogArrivedCountData === 'number' ? guestBlogArrivedCountData : 0,
        navLocked: clientGateActive,
      });
    }
    if (isAgency) {
      return buildAgencyRailItems();
    }
    return buildAdminRailItems({
      hasPermission,
      pendingSitesCount: hasPermission('pending_sites') ? (pendingSitesStats?.data?.pending || 0) : 0,
      pendingPriceCount: hasPermission('price_change') ? (typeof pendingCountData === 'number' ? pendingCountData : 0) : 0,
    });
  }, [
    isClient,
    isAgency,
    hasPermission,
    clientGateActive,
    guestBlogArrivedCountData,
    pendingSitesStats,
    pendingCountData,
    drCalcPendingCountData,
  ]);

  // Open the panel whenever a project is selected (project context view).
  useEffect(() => {
    if (selectedProject) setPanelOpen(true);
  }, [selectedProject, setPanelOpen]);

  // Global menu pages without a project context (Sites catalog, Planning
  // module) are standalone views — collapse the clients/projects sub-panel
  // there. The workflow Explore Sites step (/sites?projectId=…) keeps the
  // panel since it has a project context.
  useEffect(() => {
    const hasProject = Boolean(new URLSearchParams(location.search).get('projectId'));
    const isGlobalPage =
      location.pathname.startsWith('/sites') ||
      location.pathname.startsWith('/planning') ||
      location.pathname.startsWith('/ai-suggestions') ||
      location.pathname.startsWith('/competitor-backlink-analysis') ||
      location.pathname.startsWith('/requested-sites');
    if (isGlobalPage && !hasProject) {
      setPanelOpen(false);
    }
  }, [location.pathname, location.search, setPanelOpen]);

  // Auto-open the clients/projects panel when landing on the dashboard (Home),
  // including the initial post-login landing regardless of any redirects. It is
  // opened once per dashboard visit (so a manual collapse is respected), and the
  // decision waits for data so we only open when there's actually something to
  // show: admins/super admins need at least one client; clients need a project.
  // Other global pages keep the panel collapsed (handled by IconRail clicks).
  const dashboardHandledRef = useRef(false);
  useEffect(() => {
    if (location.pathname !== '/dashboard') {
      dashboardHandledRef.current = false; // reset so the next visit re-opens
      return;
    }
    if (selectedProject || dashboardHandledRef.current) return;

    if (isClient) {
      if (clientProjectsLoading) return; // wait for data
      dashboardHandledRef.current = true;
      setPanelOpen(clientHasProjects);
    } else if (isAgency) {
      if (agencyOnboardingLoading) return;
      dashboardHandledRef.current = true;
      setPanelOpen(hasManagedClients || hasLegacySelfProjects);
    } else if (isAdmin || isSuperAdmin) {
      if (adminClientsLoading) return;
      dashboardHandledRef.current = true;
      setPanelOpen(adminHasClients);
    }
  }, [
    location.pathname,
    selectedProject,
    isClient,
    isAdmin,
    isAgency,
    isSuperAdmin,
    clientProjectsLoading,
    clientHasProjects,
    adminClientsLoading,
    adminHasClients,
    agencyOnboardingLoading,
    hasManagedClients,
    hasLegacySelfProjects,
    setPanelOpen,
  ]);

  // Client gate: redirect away from non-dashboard routes
  useEffect(() => {
    if (!clientGateActive || clientProjectsLoading) return;
    if (urlProjectId || selectedProject) return;
    const allowed = location.pathname === '/dashboard';
    if (!allowed) {
      navigate('/dashboard', { replace: true });
    }
  }, [clientGateActive, clientProjectsLoading, location.pathname, navigate, urlProjectId, selectedProject]);

  // Agency onboarding: client first, then project (like client login flow)
  useEffect(() => {
    if (!agencyGateActive || agencyOnboardingLoading) return;
    if (urlProjectId || selectedProject) return;
    const allowed = location.pathname.startsWith('/clients');
    if (!allowed) {
      navigate('/clients', { replace: true });
    }
  }, [agencyGateActive, agencyOnboardingLoading, location.pathname, navigate, urlProjectId, selectedProject]);

  useEffect(() => {
    if (!agencyGateNoProjects || !firstAgencyClientId || agencyOnboardingLoading) return;
    if (urlProjectId || selectedProject) return;
    const clientDetailPath = `/clients/${firstAgencyClientId}`;
    if (location.pathname !== clientDetailPath) {
      navigate(clientDetailPath, { state: { promptFirstProject: true }, replace: true });
    }
  }, [
    agencyGateNoProjects,
    firstAgencyClientId,
    agencyOnboardingLoading,
    location.pathname,
    navigate,
    urlProjectId,
    selectedProject,
  ]);

  const createProjectMutation = useMutation({
    mutationFn: (data: CreateProjectData) => projectService.createProject(data),
    onSuccess: (project) => {
      completeProjectCreation(queryClient, project, {
        closeDialog: () => closeProjectForm(),
        focusClientInPanel,
        selectProject,
        clientName: isClient ? 'My account' : project.client?.name || 'Client',
      });
      void queryClient.invalidateQueries({ queryKey: ['wf-client-project-count'] });
      void queryClient.invalidateQueries({ queryKey: ['wf-agency-project-count'] });
    },
  });

  const handleProjectSubmit = async (data: CreateProjectData | UpdateProjectData) => {
    setFormLoading(true);
    try {
      await createProjectMutation.mutateAsync(data as CreateProjectData);
    } finally {
      setFormLoading(false);
    }
  };

  // The workflow step bar (Overview → … → Order Status) must ONLY show inside a
  // real project context. Several workflow steps share their path with global
  // menu pages (/sites, /guest-blog-placements, /planning), so we can't rely on
  // activeStep alone — a stale selectedProject would leak the tabs onto those
  // global pages. Genuine workflow routes always carry projectId in the URL.
  const hasProjectContext = useMemo(
    () => Boolean(new URLSearchParams(location.search).get('projectId')),
    [location.search]
  );

  const breadcrumb = useMemo(() => {
    if (selectedProject) {
      const backToProjects =
        workflowEntryPoint === 'projects' ? (
          <>
            <button
              type="button"
              className="wf-breadcrumb-link"
              onClick={() => {
                clearProject();
                setPanelOpen(true);
                navigate('/projects');
              }}
            >
              Projects
            </button>
            <span style={{ color: 'var(--text-muted)', margin: '0 4px' }}>/</span>
          </>
        ) : null;

      return (
        <>
          {backToProjects}
          <span style={{ color: 'var(--text-muted)' }}>{selectedProject.clientName}</span>
          <span style={{ color: 'var(--text-muted)', margin: '0 4px' }}>/</span>
          <span className="cur">{selectedProject.projectName}</span>
        </>
      );
    }
    const clientDetailMatch = location.pathname.match(/^\/clients\/([^/]+)$/);
    if (clientDetailMatch) {
      return (
        <>
          <span style={{ color: 'var(--text-muted)' }}>Clients</span>
          <span style={{ color: 'var(--text-muted)', margin: '0 4px' }}>/</span>
          <span className="cur">Client details</span>
        </>
      );
    }
    const titles: Record<string, string> = {
      '/dashboard': 'Dashboard',
      '/orders': 'Transactions',
      '/publishers': 'Publishers',
      '/live-link-status': 'Live Monitoring',
      '/pending-sites': 'Pending Sites',
      '/price-change-requests': 'Price Requests',
      '/users': 'Users',
      '/audit': 'Audit Logs',
      '/admin-billing': 'Billing',
      '/settings': 'Settings',
      '/competitor-backlink-site-requests': 'Competitor Backlinks',
      '/ai-suggestions': 'AI Suggestions',
      '/requested-sites': 'Requested Sites',
      '/competitor-backlink-analysis': 'Competitor Analysis',
      '/clients': 'Clients',
      '/projects': 'Projects',
      '/planning': 'Planning',
      '/sites': 'Guest Blog Sites',
      '/guest-blog-placements': 'Orders',
      '/archive': 'Archive',
    };
    const match = Object.keys(titles).find((p) => location.pathname.startsWith(p));
    return <span className="cur">{match ? titles[match] : 'Dashboard'}</span>;
  }, [selectedProject, location.pathname, workflowEntryPoint, clearProject, navigate, setPanelOpen]);

  const renderMainContent = () => {
    if ((isClient && clientProjectsLoading) || agencyOnboardingLoading) {
      return (
        <div className="wf-content" style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
          <LoadingSpinner />
        </div>
      );
    }
    if (clientGateActive && location.pathname === '/dashboard') {
      return (
        <div className="wf-content">
          <div className="wf-client-gate">
            <h2>Create your first project</h2>
            <p>
              A project is required before you can explore sites, build orders, and place orders.
              Create your first project to get started.
            </p>
            <button
              type="button"
              className="wf-btn-create wf-btn-create-xl"
              onClick={() => openProjectForm()}
            >
              <AutoAwesome sx={{ fontSize: 22 }} />
              Create Project
            </button>
          </div>
        </div>
      );
    }
    return <div className="wf-content">{children}</div>;
  };

  const initials = `${user?.firstName?.charAt(0) || ''}${user?.lastName?.charAt(0) || ''}`.toUpperCase();

  return (
    <div className="wf-shell">
      <IconRail items={railItems} disabled={clientGateActive || agencyGateActive} />
      <ClientProjectPanel
        open={panelOpen}
        onToggle={togglePanel}
        onCreateProject={openProjectForm}
        mode={isClient ? 'client' : 'admin'}
      />
      <div className="wf-main">
        <header className="wf-topbar">
          <IconButton
            size="small"
            onClick={togglePanel}
            sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            aria-label="Toggle panel"
          >
            <ViewSidebar fontSize="small" />
          </IconButton>
          <div className="wf-breadcrumb">{breadcrumb}</div>
          <div className="wf-topbar-actions">
            <NotificationBell />
            {(!isClient || !clientGateActive) && !(isAgency && agencyGateActive) && (
              <button type="button" className="wf-btn-create" onClick={() => openProjectForm()}>
                <AutoAwesome sx={{ fontSize: 14 }} />
                Create Project
              </button>
            )}
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
              <Avatar sx={{ width: 32, height: 32, background: 'linear-gradient(135deg, #2563eb, #7c3aed)', fontSize: 12 }}>
                {initials || 'U'}
              </Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem disabled>
                <ListItemIcon>
                  <AccountCircle fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {user?.firstName} {user?.lastName}
                  <Typography variant="caption" display="block">
                    {user?.email}
                  </Typography>
                </ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  logout();
                }}
              >
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </div>
        </header>
        {hasProjectContext && selectedProject && !clientGateActive && !agencyGateActive && activeStep !== null && (
          <ProjectWorkflowTabs />
        )}
        {renderMainContent()}
      </div>

      <ProjectForm
        open={projectFormOpen}
        onClose={closeProjectForm}
        onSubmit={handleProjectSubmit}
        clients={(clientsData?.data || []).map((c) => ({ id: c.id, name: c.name, email: c.email }))}
        loading={formLoading}
        isClientUser={isClient}
        userClientId={user?.clientId}
        canCreateClient={isAdmin || isAgency}
        fixedClientId={projectFormClientId || undefined}
      />
    </div>
  );
};

export default WorkflowLayout;
